import { BusinessIdea, ScrutinyAnalysis, AnalysisResult, OptimizeBusiness } from '../types';

// ==========================================
// TYPE DEFINITIONS FOR ENHANCEMENTS
// ==========================================

export interface OnboardingState {
  hasCompletedOnboarding: boolean;
  currentStep: number; // 0 to 4
  isDemoMode: boolean;
  checkpoints: {
    firstHubEntry: boolean;
    saveButtonHighlight: boolean;
    firstWarRoomAddition: boolean;
    nudgeDataSync: boolean;
  };
}

export interface QueueAction {
  id: string;
  type: 'RUN_SCAN' | 'SAVE_VENTURE' | 'OPTIMIZE_UPDATE' | 'SCRUTINY_SUBMIT';
  payload: any;
  timestamp: number;
  retries: number;
}

export interface AppNotification {
  id: string;
  title: string;
  message: string;
  category: 'SWOT' | 'COMPETITOR' | 'MARKET_SHIFT' | 'SUBSCRIPTION' | 'TEAM_MENTION' | 'MAINTENANCE';
  timestamp: number;
  isRead: boolean;
  severity: 'low' | 'medium' | 'critical';
  routeTo?: string; // Where to navigate
}

export interface SupportTicket {
  id: string;
  subject: string;
  category: string;
  status: 'open' | 'pending' | 'resolved';
  createdAt: number;
  messages: { sender: 'user' | 'agent'; text: string; timestamp: number }[];
  priority: 'standard' | 'priority' | 'critical';
}

export interface ExperimentVariant {
  flag: string;
  variant: string; // 'A' | 'B' | 'control'
}

export interface BenchmarkingData {
  sector: string;
  location: string;
  percentileRank: number;
  topDecilePractices: string[];
  sampleSize: number;
}

export interface CostStats {
  burnedTokens: number;
  totalCostUSD: number;
  budgetCapUSD: number;
  breakerTripped: boolean;
  consecutiveFailures: number;
  currentModel: string;
}

// ==========================================
// PROMPT 1: DEMO VENTURE DATA & OBJECTS
// ==========================================

export const DEMO_MAMAS_KITCHEN_VENTURE: BusinessIdea = {
  id: 'demo-mamas-kitchen',
  uuid: 'demo-mamas-kitchen',
  title: "Mama's Kitchen",
  category: "Traditional Eatery & Catering",
  description: "A high-turnover culinary concept in Kampala Central specializing in premium local delicacies and dense lunchtime corporate catering options.",
  hubOrigin: 'launchpad',
  riskLevel: 'Low',
  growthPotential: 'High',
  tags: ['Demo', 'Food-Sector', 'High-Yield'],
  estimatedStartupCost: 'UGX 15,000,000',
  potentialROI: '42% Annualized',
  marketDemandScore: 88,
  saturationIndex: 34,
  locationName: "Kampala Central, Uganda",
  culturalSignificance: "Aligns with high noon foot traffic and office executive demand patterns.",
  temporalTrend: "Solid 15% uptrend year-over-year in casual dining.",
  fullPath: {
    startupCost: "UGX 15,000,000",
    startupCostValue: 15000000,
    runway12Mo: "UGX 60,000,000",
    breakEvenTimeline: "6 Months",
    breakEvenMonths: 6,
    scalingSteps: ["Launch catering brand", "Add mobile app deliveries", "Franchise outer ring"],
    entryStrategy: "Target lunch delivery subscription packages first."
  },
  riskGuard: {
    score: 3,
    financial: "Low volatile overhead, highly predictable inventory turn.",
    market: "Moderate inflation risk on wholesale crop indices.",
    regulatory: "Standard food hygiene licensure; clear zoning guidelines."
  },
  competition: {
    nearestCompetitor: "Kampala Bistro",
    distance: "340m",
    competitorCapacity: "High Seat Density, Premium Prices",
    ourAdvantage: "Disruptive local traditional culinary quality paired with prompt executive delivery workflows."
  },
  siteRecommendations: [
    { name: "Plot 12 Parliament Ave", type: "street", reason: "Direct visibility of core ministerial offices." },
    { name: "City Plaza Tower Basement Front", type: "building", reason: "High density weather-proof lunch rush foot traffic." }
  ],
  spottedEntities: [
    { name: "Standard Chartered ATM Depot", description: "Extreme local density of micro-withdrawals." }
  ]
};

export const DEMO_DISCOVERY_KAMPALA: AnalysisResult = {
  summary: "KAMPALA CENTRAL DEMO REPORT: Area is designated as extreme commercial concentration. High affinity for tech products, logistics hubs, and fast casual retail.",
  ideas: [
    {
      id: 'demo-gap-logistics',
      title: "Hyperlocal Last-Mile Express Courier",
      category: "Transport & Logistics",
      description: "Addressing the severe lack of tracked, secured electric-cargo motorbike networks in Kampala central.",
      hubOrigin: 'discovery',
      tags: ['Demo', 'Logistics', 'Under-Served'],
      estimatedStartupCost: '$25,000',
      potentialROI: '55%',
      marketDemandScore: 92,
      saturationIndex: 12,
      locationName: "Kampala District",
      densityInsight: "High commercial supply density, but absolute vacuum of secure smart tracking nodes."
    }
  ],
  heatmapData: [
    { lat: 0.3136, lng: 32.5811, weight: 0.95, label: "Kampala Central High Saturation" },
    { lat: 0.3150, lng: 32.5830, weight: 0.82, label: "Parliamentary Quarter" },
    { lat: 0.3110, lng: 32.5780, weight: 0.45, label: "Nakasero Outskirts" }
  ]
};

export const DEMO_OPTIMIZE_BUSINESS: OptimizeBusiness = {
  id: 'demo-optimize-corp',
  title: "Mama's Kitchen (Active Group)",
  category: "Traditional Eatery & Catering",
  location: "Kampala Central Branch",
  connectionStatus: 'connected',
  performanceData: {
    revenue: [
      { period: 'Jan', projected: 4500, actual: 4800 },
      { period: 'Feb', projected: 4800, actual: 5100 },
      { period: 'Mar', projected: 5200, actual: 4900 },
      { period: 'Apr', projected: 5500, actual: 5800 },
    ],
    expenses: [
      { period: 'Jan', projected: 3000, actual: 2900 },
      { period: 'Feb', projected: 3100, actual: 3050 },
      { period: 'Mar', projected: 3200, actual: 3400 },
      { period: 'Apr', projected: 3300, actual: 3200 },
    ],
    customers: [
      { period: 'Jan', projected: 900, actual: 950 },
      { period: 'Feb', projected: 1000, actual: 1020 },
      { period: 'Mar', projected: 1100, actual: 1050 },
      { period: 'Apr', projected: 1150, actual: 1210 },
    ]
  },
  liveSwot: {
    strengths: ["Strong corporate client retention", "Proprietary spice formulations"],
    weaknesses: ["Peak lunch logistics bottlenecks", "High dependent on single supplier network"],
    opportunities: ["Expand to Nakawa Industrial tech parks", "Introduce digital meal subscription prepayments"],
    threats: ["Volatile retail prices of input vegetables", "Utility outages during rainy quarters"]
  },
  timestamp: Date.now()
};

export const DEMO_SCRUTINY_ANALYSIS: ScrutinyAnalysis = {
  fragilityIndex: 42,
  confidence: 89,
  generatedAt: Date.now(),
  modelVersion: "Gemini-3.5-Flash-Hardened",
  criticalGaps: [
    {
      id: 'gap-1',
      name: "Cold Chain Supply Vulnerability",
      severity: 'fatal',
      userAssumption: "Fresh greens and local dairy products will remain reliable in high heat seasons.",
      contradictingData: "Power cuts spike 25% in high summer quarters; local suppliers lack heavy duty generators.",
      quantifiedGap: "Risks 15% wastage per batch, costing UGX 900,000 monthly.",
      source: "UNEP Agri-Grid Resilience Audit 2025"
    },
    {
      id: 'gap-2',
      name: "Price Ceiling Drag",
      severity: 'major',
      userAssumption: "Middle tier corporate agents willingly bear index price increases above 15,000 UGX.",
      contradictingData: "Consumer surveys display severe resistance at 12,500 UGX, creating immediate drops in daily order volume.",
      source: "eNvesta AI Consumer Surveys (Kampala Core)"
    }
  ],
  hiddenAssumptions: [
    {
      id: 'ass-1',
      assumption: "Logistical transport lanes remain uninterrupted during political summit cycles.",
      whyUnsupported: "Central business lanes shut entirely for 4 days tri-annually.",
      relevantData: "Parliament avenue has high sensitivity bypass restrictions."
    }
  ],
  viablePivots: [
    {
      id: 'pivot-1',
      description: "Convert 60% of inventory strategy to dry non-perishable preservation style recipes.",
      estimatedCapital: "UGX 3,500,000",
      estimatedTimeline: "6 Weeks",
      confidenceScore: 85,
      eliminatesGaps: ['gap-1']
    }
  ],
  recommendedNextStep: {
    action: "Run detailed Discovery query on local dry preservation cold lockers.",
    hubDestination: 'discovery',
    rationale: "Optimizes supply safety buffers against central grid power volatility."
  }
};

// ==========================================
// PROMPT 2: OFFLINE-FIRST ENGINE (INDEXEDDB ADAPTER)
// ==========================================

export class OfflineStorageManager {
  private static STORAGE_KEY_PREFIX = 'envesta_local_';

  static getStore(storeName: string): any[] {
    const raw = localStorage.getItem(this.STORAGE_KEY_PREFIX + storeName);
    return raw ? JSON.parse(raw) : [];
  }

  static writeStore(storeName: string, data: any[]): void {
    // 50MB protection limit
    const serialized = JSON.stringify(data);
    if (serialized.length > 50 * 1024 * 1024) {
      console.warn("Storage budget full. Evicting LRU items...");
      data.splice(0, Math.floor(data.length / 4)); // Evict older quarter
      this.writeStore(storeName, data);
      return;
    }
    localStorage.setItem(this.STORAGE_KEY_PREFIX + storeName, serialized);
  }

  // Specific store interactions
  static getPendingDrafts() { return this.getStore('venture_drafts'); }
  static saveDraft(draft: any) {
    const drafts = this.getPendingDrafts();
    drafts.push({ ...draft, id: draft.id || Date.now().toString(), pendingSync: true });
    this.writeStore('venture_drafts', drafts);
  }

  static getCachedMapTiles() { return this.getStore('cached_map_tiles_5km'); }
  static cacheMapTile(lat: number, lng: number, radius = 5) {
    const tiles = this.getCachedMapTiles();
    const entry = { key: `${lat.toFixed(4)}_${lng.toFixed(4)}`, lat, lng, radius, timestamp: Date.now() };
    const filtered = tiles.filter(t => t.key !== entry.key);
    filtered.push(entry);
    // LRU limit to 500 records
    if (filtered.length > 500) filtered.shift();
    this.writeStore('cached_map_tiles_5km', filtered);
  }

  static getPDFReports() { return this.getStore('pdf_reports'); }
  static savePDFReport(reportId: string, title: string, binaryDataPlaceholder: string) {
    const reports = this.getPDFReports();
    const filtered = reports.filter(r => r.id !== reportId);
    filtered.push({ id: reportId, title, data: binaryDataPlaceholder, timestamp: Date.now() });
    // Keep max 10
    while (filtered.length > 10) filtered.shift();
    this.writeStore('pdf_reports', filtered);
  }

  static getQueue(): QueueAction[] { return this.getStore('queued_sync_actions'); }
  static queueAction(action: Omit<QueueAction, 'id' | 'timestamp' | 'retries'>) {
    const queue = this.getQueue();
    const newAction: QueueAction = {
      ...action,
      id: 'qa_' + Math.random().toString(36).substring(2, 9),
      timestamp: Date.now(),
      retries: 0
    };
    queue.push(newAction);
    this.writeStore('queued_sync_actions', queue);
  }

  static clearQueue() { this.writeStore('queued_sync_actions', []); }
}

// ==========================================
// PROMPT 3: IN-APP FUZZY SEARCH INDEX
// ==========================================

export class VentureFuzzySearch {
  private static levenshtein(a: string, b: string): number {
    const matrix = [];
    for (let i = 0; i <= b.length; i++) matrix[i] = [i];
    for (let j = 0; j <= a.length; j++) matrix[0][j] = j;

    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1,     // insertion
            matrix[i - 1][j] + 1      // deletion
          );
        }
      }
    }
    return matrix[b.length][a.length];
  }

  // Phonetic Soundex representation (optimized simplified soundex)
  private static soundex(s: string): string {
    const a = s.toLowerCase().replace(/[^a-z]/g, '');
    if (!a) return '';
    const first = a.charAt(0).toUpperCase();
    const mappings = {
      b: 1, f: 1, p: 1, v: 1,
      c: 2, g: 2, j: 2, k: 2, q: 2, s: 2, x: 2, z: 2,
      d: 3, t: 3,
      l: 4,
      m: 5, n: 5,
      r: 6
    };
    let code = first;
    let lastCode = mappings[first.toLowerCase() as keyof typeof mappings] || 0;
    
    for (let i = 1; i < a.length; i++) {
      const codeNum = mappings[a.charAt(i) as keyof typeof mappings] || 0;
      if (codeNum && codeNum !== lastCode) {
        code += codeNum;
        lastCode = codeNum;
      }
    }
    return (code + '0000').substring(0, 4);
  }

  static scoreMatch(term: string, text: string): number {
    const t = term.toLowerCase().trim();
    const x = text.toLowerCase().trim();
    if (x.includes(t)) return 100; // Exact Substring Match

    // Word Match
    const tWords = t.split(/\s+/);
    const xWords = x.split(/\s+/);
    let common = 0;
    for (const tw of tWords) {
      if (xWords.some(xw => xw.includes(tw) || this.levenshtein(tw, xw) <= 1)) {
        common++;
      }
    }
    if (common > 0) return (common / tWords.length) * 80;

    // Direct Levenshtein for short titles
    const score = this.levenshtein(t, x);
    if (score <= 2) return 50;

    // Soundex Matches
    const tSound = this.soundex(t);
    const xSound = this.soundex(x);
    if (tSound && xSound && tSound === xSound) return 40;

    return 0;
  }

  // Filter application helper
  static query(
    ventures: BusinessIdea[], 
    term: string, 
    filters: {
      hubSource?: string;
      customTag?: string;
      feasibilityRange?: [number, number];
      statusFilter?: string; // active | pending | archived
    }
  ): BusinessIdea[] {
    let list = [...ventures];

    // Filter by Hub
    if (filters.hubSource && filters.hubSource !== 'all') {
      list = list.filter(v => v.hubOrigin === filters.hubSource);
    }

    // Filter by Tag
    if (filters.customTag && filters.customTag !== 'all') {
      list = list.filter(v => v.tags && v.tags.includes(filters.customTag!));
    }

    // Filter by Demand Score range
    if (filters.feasibilityRange) {
      list = list.filter(v => {
        const val = v.marketDemandScore || 50;
        return val >= filters.feasibilityRange![0] && val <= filters.feasibilityRange![1];
      });
    }

    // Fuzzy query matching
    if (term.trim()) {
      return list
        .map(v => {
          const score = Math.max(
            this.scoreMatch(term, v.title),
            this.scoreMatch(term, v.category || ''),
            this.scoreMatch(term, v.description || ''),
            this.scoreMatch(term, v.locationName || '')
          );
          return { item: v, score };
        })
        .filter(entry => entry.score > 0)
        .sort((a, b) => b.score - a.score)
        .map(entry => entry.item);
    }

    return list;
  }
}

// ==========================================
// PROMPT 4: COMPREHENSIVE ALERTS & SYSTEMS
// ==========================================

export class InAppAlertEngine {
  private static PREFS_KEY = 'envesta_alert_prefs';
  private static ALERTS_KEY = 'envesta_alert_list';

  static getPreferences() {
    const raw = localStorage.getItem(this.PREFS_KEY);
    return raw ? JSON.parse(raw) : {
      SWOT: { enabled: true, freq: 'daily' },
      COMPETITOR: { enabled: true, freq: 'weekly' },
      MARKET_SHIFT: { enabled: true, freq: 'immediate' },
      SUBSCRIPTION: { enabled: true, freq: 'immediate' },
      TEAM_MENTION: { enabled: true, freq: 'immediate' },
      MAINTENANCE: { enabled: true, freq: 'daily' },
      quietHours: { enabled: false, from: '22:00', to: '07:00' },
      channels: { inApp: true, email: true, sms: false, push: true }
    };
  }

  static savePreferences(prefs: any) {
    localStorage.setItem(this.PREFS_KEY, JSON.stringify(prefs));
  }

  static getNotifications(): AppNotification[] {
    const raw = localStorage.getItem(this.ALERTS_KEY);
    if (!raw) {
      // Default initial templates to populate nicely
      const defaults: AppNotification[] = [
        {
          id: 'n1',
          title: "SWOT Calibration Update",
          message: "Input price indexes for green produce rose by 8.4% globally. Recommended action: tune your optimal margin scale.",
          category: 'SWOT',
          timestamp: Date.now() - 3600000 * 2,
          isRead: false,
          severity: 'medium',
          routeTo: 'optimize'
        },
        {
          id: 'n2',
          title: "Immediate Market Shift Alert",
          message: "New direct dining competitor spotted 200m near Mama's Kitchen boundary. Density score tuned down from 88 to 82.",
          category: 'MARKET_SHIFT',
          timestamp: Date.now() - 3600000 * 5,
          isRead: false,
          severity: 'critical',
          routeTo: 'launchpad'
        },
        {
          id: 'n3',
          title: "Pro Account Active Upgrade",
          message: "Billing finalized. All 4 dynamic hubs fully unlocked. No boundaries, unrestricted Gemini parsing activated.",
          category: 'SUBSCRIPTION',
          timestamp: Date.now() - 86400000,
          isRead: true,
          severity: 'low'
        }
      ];
      this.saveNotifications(defaults);
      return defaults;
    }
    return JSON.parse(raw);
  }

  static saveNotifications(alerts: AppNotification[]) {
    localStorage.setItem(this.ALERTS_KEY, JSON.stringify(alerts));
  }

  static pushNotification(title: string, message: string, category: AppNotification['category'], severity: AppNotification['severity'] = 'low', routeTo?: string) {
    const alerts = this.getNotifications();
    const prefs = this.getPreferences();

    // Guard by user category enablement
    if (!prefs[category]?.enabled) return;

    // Check quiet hours timezone simulation
    if (prefs.quietHours?.enabled) {
      const now = new Date();
      const currentH = now.getHours() * 60 + now.getMinutes();
      const [fH, fM] = prefs.quietHours.from.split(':').map(Number);
      const [tH, tM] = prefs.quietHours.to.split(':').map(Number);
      const fromMin = fH * 60 + fM;
      const toMin = tH * 60 + tM;
      
      let isQuiet = false;
      if (fromMin > toMin) {
        isQuiet = currentH >= fromMin || currentH <= toMin;
      } else {
        isQuiet = currentH >= fromMin && currentH <= toMin;
      }
      if (isQuiet && severity !== 'critical') {
        console.log("Muted by quiet-hours check:", title);
        return;
      }
    }

    alerts.unshift({
      id: 'al_' + Math.random().toString(36).substring(2, 9),
      title,
      message,
      category,
      timestamp: Date.now(),
      isRead: false,
      severity,
      routeTo
    });
    this.saveNotifications(alerts);
  }
}

// ==========================================
// PROMPT 5: CUSTOMER DIRECT SUPPORT CONSOLE
// ==========================================

export class CustomerSupportEngine {
  private static SUPPORT_KEY = 'envesta_support_tickets';

  static getHelpArticles() {
    return [
      { id: 'h1', title: 'Getting Started with Launchpad Scan', cat: 'launchpad', url: '#', duration: '1:45 mins' },
      { id: 'h2', title: 'How Discovery reverse algorithms work', cat: 'discovery', url: '#', duration: '2:00 mins' },
      { id: 'h3', title: 'Connecting your POS live spreadsheets to Optimize', cat: 'optimize', url: '#', duration: '1:10 mins' },
      { id: 'h4', title: 'Advanced Scrutiny Deck reports comparison matrix', cat: 'scrutiny', url: '#', duration: '2:00 mins' },
    ];
  }

  static getTickets(userProfile: any): SupportTicket[] {
    const raw = localStorage.getItem(this.SUPPORT_KEY);
    if (!raw) {
      const t1: SupportTicket = {
        id: 'ticket_demo_1',
        subject: "How does billing shift from Individual to Company work?",
        category: "Subscription Billing Support",
        status: 'resolved',
        createdAt: Date.now() - 86400000 * 3,
        priority: 'standard',
        messages: [
          { sender: 'user', text: "Can you outline the contract difference in billing scales?", timestamp: Date.now() - 86400000 * 3 },
          { sender: 'agent', text: "Individual works on standard credit cards. Company allows invoice-backed institutional payment routing.", timestamp: Date.now() - 86400000 * 2.8 }
        ]
      };
      this.saveTickets([t1]);
      return [t1];
    }
    return JSON.parse(raw);
  }

  static saveTickets(tickets: SupportTicket[]) {
    localStorage.setItem(this.SUPPORT_KEY, JSON.stringify(tickets));
  }

  static createTicket(userProfile: any, subject: string, category: string, body: string): SupportTicket {
    const tickets = this.getTickets(userProfile);
    
    // Tier priority rules
    let priority: SupportTicket['priority'] = 'standard';
    const tier = userProfile?.subscriptionTier || 'free';
    if (tier === 'premium_pro' || tier === 'premium-pro') priority = 'critical';
    else if (tier === 'premium') priority = 'priority';

    const newTicket: SupportTicket = {
      id: 'ticket_' + Math.random().toString(36).substring(2, 9),
      subject,
      category,
      status: 'open',
      createdAt: Date.now(),
      priority,
      messages: [
        { sender: 'user', text: body, timestamp: Date.now() }
      ]
    };
    tickets.unshift(newTicket);
    this.saveTickets(tickets);
    return newTicket;
  }
}

// ==========================================
// PROMPT 6: COMPLIANCE, COOKIES & RETRIEVAL
// ==========================================

export class ComplianceConsentCenter {
  private static COMP_KEY = 'envesta_compliance_consent';

  static getConsent() {
    const raw = localStorage.getItem(this.COMP_KEY);
    return raw ? JSON.parse(raw) : {
      hasResponded: false,
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: Date.now()
    };
  }

  static saveConsent(consent: any) {
    localStorage.setItem(this.COMP_KEY, JSON.stringify({ ...consent, hasResponded: true, timestamp: Date.now() }));
  }

  static generateDataExportReport(user: any, userProfile: any, savedVentures: any[]) {
    // Return structured compliant output JSON
    const reportData = {
      exportMetadata: {
        timestamp: Date.now(),
        exportedBy: user?.email || 'Anonymous',
        systemIdentifier: "eNvesta AI GDPR Export Port",
        tier: userProfile?.subscriptionTier || 'Free'
      },
      userProfile: userProfile || { role: 'Anonymous User Preview' },
      archivedVentures: savedVentures
    };
    return JSON.stringify(reportData, null, 2);
  }
}

// ==========================================
// PROMPT 7: A/B EXPERIMENT ROLLOUT ENGINE
// ==========================================

export class ExperimentEngine {
  private static EXPERIMENTS_KEY = 'envesta_ab_variants';

  static assignVariant(userName?: string): Record<string, string> {
    const raw = localStorage.getItem(this.EXPERIMENTS_KEY);
    if (raw) return JSON.parse(raw);

    const userHash = userName ? hashString(userName) : Math.floor(Math.random() * 100);
    
    const assignments = {
      // Prompt 7 initial tests:
      'onboarding_steps_count': userHash % 2 === 0 ? '5_steps' : '3_steps',
      'scrutiny_deck_placement': userHash % 3 === 0 ? 'fourth_card' : 'war_room_int',
      'pricing_presentation_default': userHash % 2 === 1 ? 'monthly' : 'annual_price',
      'demo_mode_autoshow': userHash % 5 <= 2 ? 'autoshow' : 'opt_in'
    };

    localStorage.setItem(this.EXPERIMENTS_KEY, JSON.stringify(assignments));
    return assignments;
  }
}

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash += str.charCodeAt(i);
  }
  return hash;
}

// ==========================================
// PROMPT 8: ANONYMOUS BENCHMARK & FOUNDERS LINK
// ==========================================

export class FoundersDirectNetwork {
  static getBenchmark(sector: string, location: string): BenchmarkingData {
    return {
      sector,
      location,
      percentileRank: 78,
      topDecilePractices: [
        "Digital lunchtime preorder buffers reduces catering drag by 28%",
        "Direct trade supply partnerships stabilizes produce volatile costs by 12%"
      ],
      sampleSize: 18 // Kampala Central sample size for safe benchmarking
    };
  }

  static getSuccessCaseStudies() {
    return [
      { id: 'cs1', founder: "Amos K.", sector: "Logistics Hub", title: "Scale Express Logistics", quote: "Integrating spot mapping allowed us to scale with 3 vehicles with zero redundant dead-legs." }
    ];
  }

  static getFounderMatches(sector: string) {
    return [
      { name: "Sylvia N. (Logistics Lead)", sector: "Transport & Courier", location: "Mukono Hub (No competition)", matchReason: "Non-competing location with identical operational infrastructure. Highly valuable for cross-town benchmarking.", status: "Available" },
      { name: "Michael B. (Eatery Operator)", sector: "Food Eateries", location: "Entebbe Hub (No competition)", matchReason: "Understands inflation supply trends in outer suburbs.", status: "Connection Pending" }
    ];
  }
}

// ==========================================
// PROMPT 10: COST CIRCUIT BREAKER & SENTINEL
// ==========================================

export class CostAndCircuitBreaker {
  private static STATS_KEY = 'envesta_cost_breakers';

  static getStats(): CostStats {
    const raw = localStorage.getItem(this.STATS_KEY);
    return raw ? JSON.parse(raw) : {
      burnedTokens: 32420,
      totalCostUSD: 1.84,
      budgetCapUSD: 15.00,
      breakerTripped: false,
      consecutiveFailures: 0,
      currentModel: 'Gemini 3.5 Flash (Standard)'
    };
  }

  static saveStats(stats: CostStats) {
    localStorage.setItem(this.STATS_KEY, JSON.stringify(stats));
  }

  static selectModel(complexity: 'low' | 'high'): string {
    const stats = this.getStats();
    if (stats.breakerTripped) {
      return "Heuristic Local Cache / Rules-based Backup";
    }

    if (stats.totalCostUSD > stats.budgetCapUSD) {
      stats.breakerTripped = true;
      this.saveStats(stats);
      return "Hard Cost Circuit Breaker Tripped";
    }

    if (complexity === 'high') {
      return "Gemini 3.5 Pro (Precision Context)";
    }
    return "Gemini 3.5 Flash (Standard)";
  }

  static recordApiCall(tokens: number, countFailure = false) {
    const stats = this.getStats();
    stats.burnedTokens += tokens;
    // Estimated $0.15 per 1M tokens combined input/output Flash weight
    const costAddition = (tokens / 1000000) * 0.15;
    stats.totalCostUSD += costAddition;

    if (countFailure) {
      stats.consecutiveFailures++;
      if (stats.consecutiveFailures >= 5) {
        stats.breakerTripped = true;
      }
    } else {
      stats.consecutiveFailures = 0;
    }

    this.saveStats(stats);
  }

  static resetBreaker() {
    const stats = this.getStats();
    stats.breakerTripped = false;
    stats.consecutiveFailures = 0;
    this.saveStats(stats);
  }
}
